import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { garagesApi, uploadUrl, type GarageCarApi } from '../../api/client';
import { CommentList } from '../../components/CommentList/CommentList';
import { AuthRequiredModal } from '../../components/AuthRequiredModal/AuthRequiredModal';
import type { GarageCar, GarageMod, TrackSession } from '../../types';
import { parseGarageBackPath, garageBackLinkLabel, type GarageCarNavState } from '../../navigation/garageCarNav';
import styles from './GarageCarDetail.module.css';

/** YouTube URL helpers */
function isYoutubeUrl(url: string): boolean {
  if (!url || typeof url !== 'string') return false;
  return /youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\//i.test(url.trim());
}

function getYoutubeVideoId(url: string): string | null {
  if (!isYoutubeUrl(url)) return null;
  const u = url.trim();
  const m = u.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/i);
  return m ? m[1] : null;
}

function getYoutubeThumbnail(url: string): string {
  const id = getYoutubeVideoId(url);
  return id ? `https://img.youtube.com/vi/${id}/mqdefault.jpg` : '';
}

function getYoutubeEmbedUrl(url: string): string {
  const id = getYoutubeVideoId(url);
  return id ? `https://www.youtube.com/embed/${id}?autoplay=1` : '';
}

/** Format YYYY-MM-DD or ISO date string to local MM/DD/YYYY */
function formatDateLocal(dateStr: string | undefined): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const year = d.getFullYear();
  return `${month}/${day}/${year}`;
}

/** Format price for display with $ and thousands separators if it looks like a number */
function formatSalePrice(value: string | undefined): string {
  if (!value || !value.trim()) return '';
  const trimmed = value.trim();
  if (trimmed.startsWith('$')) return trimmed;
  const num = trimmed.replace(/[^0-9.]/g, '');
  if (num && !Number.isNaN(Number(num))) {
    return '$' + Number(num).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }
  return '$' + trimmed;
}

function garageApiToCar(g: GarageCarApi): GarageCar {
  const images = Array.isArray(g.images) ? g.images : (Array.isArray(g.photos) ? g.photos : []).map((url) => ({ url, caption: undefined }));
  const photos = Array.isArray(g.photos) ? g.photos : images.map((i) => i.url);
  const garageMods = Array.isArray(g.garageMods)
    ? g.garageMods.map((m) => ({
        id: m.id,
        garageCarId: m.garageCarId,
        category: m.category as GarageMod['category'],
        title: m.title,
        details: m.details,
        dateCompleted: m.dateCompleted,
        images: m.images ?? [],
        createdAt: m.createdAt,
        updatedAt: m.updatedAt,
      }))
    : undefined;
  return {
    id: g.id,
    ownerId: g.ownerId,
    name: g.name,
    car: g.car as GarageCar['car'],
    year: g.year,
    trim: g.trim,
    engine: g.engine,
    mods: g.mods,
    garageMods,
    trackSessions: Array.isArray(g.trackSessions) ? g.trackSessions.map((s) => ({ ...s, garageCarId: s.garageCarId || g.id })) : undefined,
    performanceSpecs: g.performanceSpecs,
    colorCode: g.colorCode,
    colorName: g.colorName,
    vin: g.vin,
    currentOdometer: g.currentOdometer,
    averageMilesPerMonth: g.averageMilesPerMonth,
    maintenanceRemindersEnabled: g.maintenanceRemindersEnabled,
    lastOdometerUpdatedAt: g.lastOdometerUpdatedAt,
    images,
    photos,
    buildProgressPhotos: g.buildProgressPhotos,
    isPastCar: g.isPastCar,
    acquiredDate: g.acquiredDate,
    soldDate: g.soldDate,
    forSale: g.isForSale,
    saleSellingDetails: g.saleSellingDetails,
    saleCarCondition: g.saleCarCondition,
    saleCarfaxLink: g.saleCarfaxLink,
    saleCarFeatures: g.saleCarFeatures,
    salePrice: g.salePrice,
    saleMilesDriven: g.saleMilesDriven,
    likes: Array.isArray(g.likes) ? g.likes : [],
    comments: Array.isArray(g.comments) ? g.comments.map((comment) => ({ ...comment })) : [],
    maintenanceRecords: g.maintenanceRecords,
    maintenanceSchedule: g.maintenanceSchedule,
    createdAt: g.createdAt,
    updatedAt: g.updatedAt,
  };
}

export function GarageCarDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const navState = location.state as GarageCarNavState | null | undefined;
  const fromPath = parseGarageBackPath(navState?.garageBack);
  const { user, isAuthenticated, getAuthor } = useAuth();
  const { garageCars, removeGarageCar } = useData();
  const [fetchedCar, setFetchedCar] = useState<GarageCar | null>(null);
  const [fetchDone, setFetchDone] = useState(false);
  const [imageIndex, setImageIndex] = useState(0);
  const [selectedMod, setSelectedMod] = useState<GarageMod | null>(null);
  const [expandedModImage, setExpandedModImage] = useState<string | null>(null);
  const [saleDetailsOpen, setSaleDetailsOpen] = useState(false);
  const [bestLapTimesOpen, setBestLapTimesOpen] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [likeSubmitting, setLikeSubmitting] = useState(false);
  const [engagementError, setEngagementError] = useState<string | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);

  const carFromContext = id ? garageCars.find((c) => c.id === id) : null;
  const car = useMemo(() => {
    if (carFromContext && fetchedCar) {
      return {
        ...carFromContext,
        likes: fetchedCar.likes,
        comments: fetchedCar.comments,
      };
    }
    return fetchedCar ?? carFromContext;
  }, [carFromContext, fetchedCar]);

  const loadPublicCar = useCallback(async (carId: string) => {
    setFetchDone(false);
    try {
      const api = await garagesApi.getCar(carId);
      setFetchedCar(garageApiToCar(api));
    } catch {
      setFetchedCar(null);
    } finally {
      setFetchDone(true);
    }
  }, []);

  useEffect(() => {
    if (id) {
      loadPublicCar(id);
    }
  }, [id, loadPublicCar]);

  if (!id) {
    const fallback = fromPath ?? '/garage';
    return (
      <div className={styles.page}>
        <p>Invalid car.</p>
        <Link to={fallback}>← {garageBackLinkLabel(fallback)}</Link>
      </div>
    );
  }

  if (!car) {
    const fallback = fromPath ?? '/garage';
    return (
      <div className={styles.page}>
        {!carFromContext && !fetchDone ? <p>Loading…</p> : <p>Car not found.</p>}
        <Link to={fallback}>← {garageBackLinkLabel(fallback)}</Link>
      </div>
    );
  }

  const isOwner = isAuthenticated && user && car.ownerId === user.id;
  const title = [car.car.year, car.car.make, car.car.model].filter(Boolean).join(' ');
  const images = Array.isArray(car.images) && car.images.length > 0
    ? car.images
    : (Array.isArray(car.photos) ? car.photos : []).map((url) => ({ url, caption: undefined as string | undefined }));
  const currentImage = images[imageIndex % images.length];
  const garageModsList = (car.garageMods ?? []).slice().sort((a, b) => {
    const dA = a.dateCompleted || '';
    const dB = b.dateCompleted || '';
    return dB.localeCompare(dA);
  });
  const garageSessionsList = (car.trackSessions ?? []).slice().sort((a, b) => {
    const typeA = a.eventType || '';
    const typeB = b.eventType || '';
    if (typeA !== typeB) return typeA.localeCompare(typeB);
    const venueA = a.venueName || '';
    const venueB = b.venueName || '';
    if (venueA !== venueB) return venueA.localeCompare(venueB);
    return (b.sessionDate || '').localeCompare(a.sessionDate || '');
  });
  /** Best lap per venue: for each venue, the session with the fastest bestLapTimeSeconds */
  const sessionsWithBest = (car.trackSessions ?? []).filter((s) => s.bestLapTimeSeconds != null);
  const bestLapByVenueList = Object.values(
    sessionsWithBest.reduce<Record<string, TrackSession>>((acc, s) => {
      const key = s.venueName.trim() || 'Unknown';
      const existing = acc[key];
      if (!existing || (s.bestLapTimeSeconds! < (existing.bestLapTimeSeconds ?? Infinity))) {
        acc[key] = s;
      }
      return acc;
    }, {})
  ).map((s) => ({ venue: s.venueName.trim() || 'Unknown', bestLapTimeDisplay: s.bestLapTimeDisplay, sessionDate: s.sessionDate })).sort((a, b) => a.venue.localeCompare(b.venue));
  const buildProgressPhotos = Array.isArray(car.buildProgressPhotos) ? car.buildProgressPhotos : [];
  const performanceSpecsObj =
    car.performanceSpecs && typeof car.performanceSpecs === 'object' && !Array.isArray(car.performanceSpecs)
      ? car.performanceSpecs
      : null;
  const liked = !!(user && car.likes.includes(user.id));
  const commentCountLabel = `${car.comments.length} ${car.comments.length === 1 ? 'comment' : 'comments'}`;

  const requireAuth = () => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return true;
    }
    return false;
  };

  const handleDelete = async () => {
    if (!window.confirm('Remove this car from your garage?')) return;
    try {
      await removeGarageCar(car.id);
      navigate('/garage');
    } catch (_) {}
  };

  const handleLike = async () => {
    if (requireAuth() || !id) return;
    setEngagementError(null);
    setLikeSubmitting(true);
    try {
      const api = liked ? await garagesApi.unlikeCar(id) : await garagesApi.likeCar(id);
      setFetchedCar(garageApiToCar(api));
    } catch (e) {
      setEngagementError(e instanceof Error ? e.message : 'Could not update like.');
    } finally {
      setLikeSubmitting(false);
    }
  };

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (requireAuth() || !id || !commentText.trim()) return;
    setEngagementError(null);
    setCommentSubmitting(true);
    try {
      const api = await garagesApi.addCarComment(id, commentText.trim());
      setFetchedCar(garageApiToCar(api));
      setCommentText('');
    } catch (e) {
      setEngagementError(e instanceof Error ? e.message : 'Could not post comment.');
    } finally {
      setCommentSubmitting(false);
    }
  };

  const defaultBack = isOwner ? '/garage' : `/garages/user/${car.ownerId}`;
  const backTo = fromPath ?? defaultBack;
  const backLabel = fromPath ? garageBackLinkLabel(fromPath) : isOwner ? 'Garage' : "Owner's garage";

  return (
    <div className={styles.page}>
      <Link to={backTo} className={styles.back}>← {backLabel}</Link>

      <article className={styles.card}>
        <div className={styles.gallery}>
          {currentImage ? (
            <>
              <img src={currentImage.url.startsWith('http') ? currentImage.url : uploadUrl(currentImage.url)} alt={title} className={styles.mainImage} />
              {car.forSale && <span className={styles.forSaleBanner}>For Sale</span>}
              {images.length > 1 && (
                <>
                  <button
                    type="button"
                    className={styles.carouselPrev}
                    onClick={() => setImageIndex((i) => (i <= 0 ? images.length - 1 : i - 1))}
                    aria-label="Previous image"
                  />
                  <button
                    type="button"
                    className={styles.carouselNext}
                    onClick={() => setImageIndex((i) => (i >= images.length - 1 ? 0 : i + 1))}
                    aria-label="Next image"
                  />
                  <div className={styles.carouselDots}>
                    {images.map((_, i) => (
                      <button
                        key={i}
                        type="button"
                        className={imageIndex === i ? styles.dotActive : styles.dot}
                        onClick={() => setImageIndex(i)}
                        aria-label={`Go to image ${i + 1}`}
                      />
                    ))}
                  </div>
                </>
              )}
              {currentImage.caption && (
                <p className={styles.imageCaption}>{currentImage.caption}</p>
              )}
            </>
          ) : (
            <div className={styles.placeholder}>🚗</div>
          )}
        </div>

        <div className={styles.contentLayout}>
          <div className={styles.body}>
            <h1 className={styles.title}>{title} 
              {car.car.trim && ` (${car.car.trim})`}
            </h1>
            {car.name && <p className={styles.nickname}>{car.name}</p>}
            {car.car.bodyStyle && <span className={styles.badge}>{car.car.bodyStyle}</span>}
            {car.forSale && (
              <button type="button" className={styles.saleDetailsLink} onClick={() => setSaleDetailsOpen(true)}>
                Sale Details
              </button>
            )}
            <div className={styles.engagementBar}>
              <button
                type="button"
                className={liked ? styles.likeActive : styles.likeBtn}
                onClick={handleLike}
                disabled={likeSubmitting}
              >
                ♥ {car.likes.length}
              </button>
              <span className={styles.engagementMeta}>{commentCountLabel}</span>
            </div>
            {engagementError && <p className={styles.engagementError}>{engagementError}</p>}

            {(car.engine || performanceSpecsObj?.['Engine Type'] || performanceSpecsObj?.['HP'] || performanceSpecsObj?.['Torque'] || performanceSpecsObj?.['Transmission'] || performanceSpecsObj?.['Curb weight']) && (
              <section className={styles.section}>
                <dl className={styles.specList}>
                {car.engine && (
                    <React.Fragment>
                      <dt>Engine</dt>
                      <dd>{car.engine}</dd>
                    </React.Fragment>
                  )}
                  {performanceSpecsObj?.['Engine Type'] && (
                    <React.Fragment>
                      <dt>Type</dt>
                      <dd>{performanceSpecsObj['Engine Type']}</dd>
                    </React.Fragment>
                  )}
                  {performanceSpecsObj?.['HP'] && (
                    <React.Fragment>
                      <dt>HP</dt>
                      <dd>{performanceSpecsObj['HP']}</dd>
                    </React.Fragment>
                  )}
                  {performanceSpecsObj?.['Torque'] && (
                    <React.Fragment>
                      <dt>Torque</dt>
                      <dd>{performanceSpecsObj['Torque']}</dd>
                    </React.Fragment>
                  )}
                  {performanceSpecsObj?.['Transmission'] && (
                    <React.Fragment>
                      <dt>Transmission</dt>
                      <dd>{performanceSpecsObj['Transmission']}</dd>
                    </React.Fragment>
                  )}
                  {performanceSpecsObj?.['Curb weight'] && (
                    <React.Fragment>
                      <dt>Curb weight</dt>
                      <dd>{performanceSpecsObj['Curb weight']}</dd>
                    </React.Fragment>
                  )}
                </dl>
              </section>
            )}

            {performanceSpecsObj && Object.entries(performanceSpecsObj).filter(([k]) => !['Engine Type', 'HP', 'Torque', 'Transmission', 'Curb weight'].includes(k)).length > 0 && (
            <section className={styles.section}>
              <h2>Performance</h2>
              <dl className={styles.specList}>
                {Object.entries(performanceSpecsObj)
                  .filter(([k]) => !['Engine Type', 'HP', 'Torque', 'Transmission', 'Curb weight'].includes(k))
                  .map(([k, v]) => (
                    <React.Fragment key={k}>
                      <dt>{k}</dt>
                      <dd>{v}</dd>
                    </React.Fragment>
                  ))}
              </dl>
            </section>
          )}

          {(car.colorName || car.colorCode) && (
            <section className={styles.section}>
              <h2>Color</h2>
              <p>{[car.colorName, car.colorCode].filter(Boolean).join(' · ')}</p>
            </section>
          )}

          {car.vin && (
            <section className={styles.section}>
              <h2>VIN</h2>
              <p className={styles.vin}>{car.vin}</p>
            </section>
          )}

          {buildProgressPhotos.length > 0 && (
            <section className={styles.section}>
              <h2>Build progress</h2>
              <div className={styles.buildGrid}>
                {buildProgressPhotos.map((b, i) => (
                  <figure key={i}>
                    <img src={b.url.startsWith('http') ? b.url : uploadUrl(b.url)} alt={b.caption ?? 'Build progress'} />
                    {b.caption && <figcaption>{b.caption}</figcaption>}
                    {b.date && <span className={styles.buildDate}>{formatDateLocal(b.date)}</span>}
                  </figure>
                ))}
              </div>
            </section>
          )}

          {(car.acquiredDate || car.soldDate) && (
            <section className={styles.section}>
              <h2>Ownership</h2>
              <p>
                {car.acquiredDate && <>Acquired: {formatDateLocal(car.acquiredDate)}</>}
                {car.acquiredDate && car.soldDate && ' · '}
                {car.soldDate && <>Sold: {formatDateLocal(car.soldDate)}</>}
              </p>
            </section>
          )}

          <section className={styles.section}>
            <div className={styles.commentsHeader}>
              <h2>Comments</h2>
              <span className={styles.engagementMeta}>{commentCountLabel}</span>
            </div>
            {car.comments.length > 0 ? (
              <CommentList comments={car.comments} getAuthor={getAuthor} />
            ) : (
              <p className={styles.emptyState}>No comments yet.</p>
            )}
            {isAuthenticated ? (
              <form onSubmit={handleSubmitComment} className={styles.commentForm}>
                <input
                  type="text"
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  className={styles.commentInput}
                  placeholder="Add a comment..."
                />
                <button
                  type="submit"
                  className={styles.commentSubmit}
                  disabled={commentSubmitting || !commentText.trim()}
                >
                  {commentSubmitting ? 'Posting...' : 'Post'}
                </button>
              </form>
            ) : (
              <button type="button" className={styles.secondaryBtn} onClick={() => setShowAuthModal(true)}>
                Sign in to like or comment
              </button>
            )}
          </section>

            {isOwner && (
              <div className={styles.actions}>
                <Link to={`/garage/${car.id}/edit`} state={navState ?? undefined} className={styles.editBtn}>
                  Edit
                </Link>
                <button type="button" className={styles.deleteBtn} onClick={handleDelete}>
                  Remove from garage
                </button>
              </div>
            )}
          </div>

          {(garageModsList.length > 0 || garageSessionsList.length > 0) && (
            <aside className={styles.modsSidebar}>
              {garageModsList.length > 0 && (
                <>
                  <h2 className={styles.modsSidebarTitle}>Mods</h2>
                  <ul className={styles.modsSidebarList}>
                    {garageModsList.map((mod) => (
                      <li key={mod.id}>
                        <button
                          type="button"
                          className={styles.modTitleLink}
                          onClick={() => setSelectedMod(mod)}
                        >
                          {mod.title}
                        </button>
                        {mod.dateCompleted && (
                          <span className={styles.modDate}>{formatDateLocal(mod.dateCompleted)}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {garageSessionsList.length > 0 && (
                <>
                  <h2 className={styles.modsSidebarTitle}>Track Sessions</h2>
                  {bestLapByVenueList.length > 0 && (
                    <button type="button" className={styles.saleDetailsLink} onClick={() => setBestLapTimesOpen(true)}>
                      Best Lap times
                    </button>
                  )}
                  <div className={styles.sessionsTableWrap}>
                    <table className={styles.sessionsTable}>
                      <thead>
                        <tr>
                          <th>Type</th>
                          <th>Venue</th>
                          <th>Date</th>
                          <th>Best lap</th>
                          <th>Laps</th>
                        </tr>
                      </thead>
                      <tbody>
                        {garageSessionsList.map((session) => (
                          <tr key={session.id}>
                            <td>{session.eventType}</td>
                            <td>{session.venueName}</td>
                            <td>{formatDateLocal(session.sessionDate)}</td>
                            <td>{session.bestLapTimeDisplay || '—'}</td>
                            <td>{session.lapCount != null ? session.lapCount : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </aside>
          )}
        </div>
      </article>

      {saleDetailsOpen && car.forSale && (
        <div className={styles.modalOverlay} onClick={() => setSaleDetailsOpen(false)} role="presentation">
          <div className={styles.modal} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="sale-details-title">
            <h2 id="sale-details-title">Sale Details</h2>
            <div className={styles.saleDetailsContent}>
              {car.salePrice && (
                <p className={styles.salePrice}>{formatSalePrice(car.salePrice)}</p>
              )}
              {car.saleMilesDriven != null && car.saleMilesDriven > 0 && (
                <p className={styles.saleMiles}>{car.saleMilesDriven.toLocaleString()} miles</p>
              )}
              {car.saleSellingDetails && (
                <div className={styles.saleDetailsSection}>
                  <p>{car.saleSellingDetails}</p>
                </div>
              )}
              {car.saleCarCondition && (
                <div className={styles.saleDetailsSection}>
                  <h3>Condition</h3>
                  <p>{car.saleCarCondition}</p>
                </div>
              )}
              {car.saleCarFeatures && (
                <div className={styles.saleDetailsSection}>
                  <h3>Features</h3>
                  <p className={styles.saleFeatures}>{car.saleCarFeatures}</p>
                </div>
              )}
              {car.saleCarfaxLink && (
                <div className={styles.saleDetailsSection}>
                  <h3>Carfax</h3>
                  <a href={car.saleCarfaxLink} target="_blank" rel="noopener noreferrer" className={styles.saleCarfaxLink}>
                    View report
                  </a>
                </div>
              )}
              {!car.salePrice && car.saleMilesDriven == null && !car.saleSellingDetails && !car.saleCarCondition && !car.saleCarfaxLink && !car.saleCarFeatures && (
                <p className={styles.saleDetailsEmpty}>No sale details entered yet.</p>
              )}
            </div>
            <button type="button" className={styles.modalClose} onClick={() => setSaleDetailsOpen(false)}>
              Close
            </button>
          </div>
        </div>
      )}

      {bestLapTimesOpen && bestLapByVenueList.length > 0 && (
        <div className={styles.modalOverlay} onClick={() => setBestLapTimesOpen(false)} role="presentation">
          <div className={styles.modal} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="best-lap-title">
            <h2 id="best-lap-title">Best Lap times by venue</h2>
            <div className={styles.saleDetailsContent}>
              <ul className={styles.bestLapList}>
                {bestLapByVenueList.map(({ venue, bestLapTimeDisplay, sessionDate }) => (
                  <li key={venue}>
                    <strong>{venue}</strong> — {bestLapTimeDisplay}
                    {sessionDate && <span className={styles.modDate}> ({formatDateLocal(sessionDate)})</span>}
                  </li>
                ))}
              </ul>
            </div>
            <button type="button" className={styles.modalClose} onClick={() => setBestLapTimesOpen(false)}>
              Close
            </button>
          </div>
        </div>
      )}

      {selectedMod && (
        <div className={styles.modalOverlay} onClick={() => { setSelectedMod(null); setExpandedModImage(null); }} role="presentation">
          <div className={styles.modal} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <h2>{selectedMod.title}</h2>
            <p className={styles.modCategory}>{selectedMod.category}</p>
            {selectedMod.dateCompleted && (
              <p className={styles.modMeta}>Completed: {formatDateLocal(selectedMod.dateCompleted)}</p>
            )}
            {selectedMod.details && (
              <p className={styles.modDetails}>{selectedMod.details}</p>
            )}
            {selectedMod.images && selectedMod.images.length > 0 && (
              <div className={styles.modImages}>
                {selectedMod.images.map((img, i) => {
                  const src = img.url.startsWith('http') ? img.url : uploadUrl(img.url);
                  const isYoutube = isYoutubeUrl(src);
                  const thumbSrc = isYoutube ? getYoutubeThumbnail(src) : src;
                  return (
                    <figure key={i}>
                      <button
                        type="button"
                        className={styles.modImageButton}
                        onClick={() => setExpandedModImage(src)}
                      >
                        <img src={thumbSrc} alt={img.caption ?? 'Mod'} />
                        {isYoutube && <span className={styles.youtubePlayOverlay} aria-hidden>▶</span>}
                      </button>
                      {img.caption && <figcaption>{img.caption}</figcaption>}
                    </figure>
                  );
                })}
              </div>
            )}
            {isOwner && (
              <Link
                to={`/garage/${car.id}/edit`}
                state={navState ?? undefined}
                className={styles.editBtn}
                onClick={() => {
                  setSelectedMod(null);
                  setExpandedModImage(null);
                }}
              >
                Edit mod
              </Link>
            )}
            <button type="button" className={styles.modalClose} onClick={() => { setSelectedMod(null); setExpandedModImage(null); }}>
              Close
            </button>
          </div>
        </div>
      )}

      {expandedModImage && (
        <div
          className={styles.imageExpandedOverlay}
          onClick={() => setExpandedModImage(null)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Escape' && setExpandedModImage(null)}
          aria-label="Close expanded view"
        >
          {isYoutubeUrl(expandedModImage) ? (
            <div className={styles.youtubeEmbedWrap} onClick={(e) => e.stopPropagation()}>
              <iframe
                title="YouTube video"
                src={getYoutubeEmbedUrl(expandedModImage)}
                className={styles.youtubeEmbed}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : (
            <img
              src={expandedModImage}
              alt="Expanded view of modification"
              className={styles.imageExpandedImg}
              onClick={(e) => e.stopPropagation()}
            />
          )}
          <button
            type="button"
            className={styles.imageExpandedClose}
            onClick={() => setExpandedModImage(null)}
            aria-label="Close"
          >
            ×
          </button>
        </div>
      )}
      {showAuthModal && <AuthRequiredModal onClose={() => setShowAuthModal(false)} />}
    </div>
  );
}
